import React, { useState, useEffect } from 'react';
import {
  Package, ShoppingCart, Clock, CheckCircle,
  Loader2, AlertTriangle, Info
} from 'lucide-react';
import {
  LifetimeAddon,
  LifetimeAddonStoreCoreProps,
  WorkspaceLifetimeAddon,
} from '../widget/types';

function describeGrants(addon: LifetimeAddon, quantity = 1): string {
  if (addon.grants_json) {
    return Object.entries(addon.grants_json)
      .filter(([field]) => field !== 'mau_users')
      .map(([field, amount]) => {
        const total = amount * quantity;
        const label = field
          .replace(/_/g, ' ')
          .replace(/\b\w/g, (c) => c.toUpperCase());
        return `+${total} ${label}`;
      })
      .join(' • ');
  }
  if (addon.affects_field) {
    const total = addon.quantity_per_unit * quantity;
    const label = addon.affects_field.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    return `+${total} ${label}`;
  }
  return '';
}

function isRefundEligible(addon: WorkspaceLifetimeAddon): boolean {
  if (addon.source !== 'purchase') return false;
  if (addon.refunded_at) return false;
  if (!addon.refund_eligible_until) return false;
  return new Date(addon.refund_eligible_until) > new Date();
}

function daysUntilRefundExpiry(addon: WorkspaceLifetimeAddon): number | null {
  if (!addon.refund_eligible_until) return null;
  const diff = new Date(addon.refund_eligible_until).getTime() - Date.now();
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
}

const LifetimeAddonStoreCore: React.FC<LifetimeAddonStoreCoreProps> = ({
  availableAddons,
  myAddons,
  loading,
  hasPaymentMethod,
  billingProfile,
onPurchase,
  onEstimate,
  onRefund,
  onSuccess,
  onError,
  theme,
  mode,
  reviewSubmissions,
  reviewCredits,
  canSubmitReview,
  myWorkspaces,
  onSubmitReview,
  onRedeemCredit,
}) => {
  const [activeTab, setActiveTab] = useState<'store' | 'my-addons' | 'earn'>('store');
const [purchasing, setPurchasing] = useState<number | null>(null);
  const [purchaseNotice, setPurchaseNotice] = useState<string | null>(null);
  const [refunding, setRefunding] = useState<number | null>(null);
  const [quantities, setQuantities] = useState<Record<number, number>>({});
  const [refundConfirm, setRefundConfirm] = useState<number | null>(null);
const [purchaseConfirm, setPurchaseConfirm] = useState<{ addon: LifetimeAddon; quantity: number } | null>(null);
const [purchaseConsent, setPurchaseConsent] = useState(false);
  const [estimate, setEstimate] = useState<{ sub_total: number; tax_amount: number; total: number; tax_rate: number } | null>(null);
  const [estimateLoading, setEstimateLoading] = useState(false);
  useEffect(() => {
    if (!purchaseConfirm || !onEstimate) { setEstimate(null); setEstimateLoading(false); return; }
    let cancelled = false;
    setEstimate(null);
    setEstimateLoading(true);
    onEstimate(purchaseConfirm.addon.id, purchaseConfirm.quantity)
      .then((e) => { if (!cancelled) setEstimate(e); })
      .catch(() => { if (!cancelled) setEstimate(null); })
      .finally(() => { if (!cancelled) setEstimateLoading(false); });
    return () => { cancelled = true; };
  }, [purchaseConfirm, onEstimate]);
  const _cfPrice = purchaseConfirm ? parseFloat(String(purchaseConfirm.addon.price_usd)) * purchaseConfirm.quantity : 0;
  const cfSub = estimate ? estimate.sub_total : _cfPrice;
  const cfTax = estimate ? estimate.tax_amount : 0;
  const cfTotal = estimate ? estimate.total : _cfPrice;

  // Submit Review modal state (Phase 9)
  const [submitModal, setSubmitModal] = useState<'g2' | 'capterra' | 'sourceforge' | null>(null);
  const [reviewUrl, setReviewUrl] = useState('');
  const [screenshotUrl, setScreenshotUrl] = useState('');
  const [submitConsent, setSubmitConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const closeSubmitModal = () => {
    setSubmitModal(null);
    setReviewUrl('');
    setScreenshotUrl('');
    setSubmitConsent(false);
    setSubmitError(null);
  };

  const isValidUrl = (s: string): boolean => {
    if (!s) return false;
    try { new URL(s); return true; } catch { return false; }
  };

  const handleSubmitReview = async () => {
    if (!submitModal || !onSubmitReview) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await onSubmitReview(submitModal, reviewUrl, screenshotUrl || undefined);
      onSuccess?.('Review submitted for admin approval.');
      closeSubmitModal();
    } catch (err: any) {
      setSubmitError(err?.message || 'Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };

  // Credit redemption state (management mode)
  const [selectedWorkspace, setSelectedWorkspace] = useState<Record<number, number>>({});
  const [redeeming, setRedeeming] = useState<number | null>(null);
  const [redeemError, setRedeemError] = useState<Record<number, string | null>>({});

  const handleRedeemCredit = async (creditId: number) => {
    if (!onRedeemCredit) return;
    const wsId = selectedWorkspace[creditId];
    if (!wsId) return;
    setRedeeming(creditId);
    setRedeemError((prev) => ({ ...prev, [creditId]: null }));
    try {
      await onRedeemCredit(creditId, wsId);
      const wsName = myWorkspaces?.find((w) => w.id === wsId)?.name || 'workspace';
      onSuccess?.(`Credit redeemed to ${wsName}.`);
      setSelectedWorkspace((prev) => {
        const { [creditId]: _, ...rest } = prev;
        return rest;
      });
    } catch (err: any) {
      setRedeemError((prev) => ({ ...prev, [creditId]: err?.message || 'Redemption failed' }));
    } finally {
      setRedeeming(null);
    }
  };

  // Defensive theme merge — caller may pass Partial<WidgetTheme>, so we merge
  // their overrides onto our full defaults. Every field is guaranteed populated at runtime.
  const defaults = {
    colors: {
      bg: 'var(--v2-surface)',
      bgAlt: 'var(--v2-surface-alt)',
      border: 'var(--v2-border)',
      primary: 'var(--v2-primary)',
      primaryHover: 'var(--v2-primary-hover)',
      primaryLight: 'var(--v2-primary-light)',
      text: 'var(--v2-text)',
      textMuted: 'var(--v2-text-muted)',
      textTertiary: 'var(--v2-text-tertiary)',
      success: 'var(--v2-success)',
      successBg: 'var(--v2-success-bg)',
      error: 'var(--v2-error)',
      errorBg: 'var(--v2-error-bg)',
      warning: 'var(--v2-warning)',
      infoBg: 'var(--v2-info-bg)',
    },
    radius: { sm: 'var(--radius-sm)', md: 'var(--radius-md)', lg: 'var(--radius-lg)' },
    fontSans: 'var(--font-sans)',
    fontMono: 'var(--font-mono)',
  };
  const t = {
    ...defaults,
    ...theme,
    colors: { ...defaults.colors, ...(theme?.colors || {}) },
    radius: { ...defaults.radius, ...(theme?.radius || {}) },
  };

  const getQuantity = (addonId: number) => quantities[addonId] || 1;

  const handlePurchase = async (addon: LifetimeAddon & { _confirmedQty?: number }) => {
    const qty = addon._confirmedQty ?? getQuantity(addon.id);
    setPurchasing(addon.id);
    try {
await onPurchase(addon.id, qty);
      const successMsg = `${addon.addon_name} × ${qty} added to your workspace!`;
      onSuccess?.(successMsg);
      // The widget embed hands onSuccess to the host, which may surface nothing,
      // so show our own confirmation in widget mode. Management already gets a
      // toast via onSuccess → showSuccess, so we don't double up there.
      if (mode === 'widget') {
        setPurchaseNotice(successMsg);
        setTimeout(() => setPurchaseNotice(null), 5000);
      }
      setQuantities((prev) => ({ ...prev, [addon.id]: 1 }));
    } catch (err: any) {
      onError?.(err.message || 'Purchase failed');
    } finally {
      setPurchasing(null);
    }
  };

  const handleRefund = async (wla: WorkspaceLifetimeAddon) => {
    setRefunding(wla.id);
    setRefundConfirm(null);
    try {
      await onRefund(wla.id);
      onSuccess?.('Refund processed. Allocations updated.');
    } catch (err: any) {
      onError?.(err.message || 'Refund failed');
    } finally {
      setRefunding(null);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '40px', fontFamily: t.fontSans }}>
        <Loader2 size={24} strokeWidth={1.5} color={t.colors.primary} style={{ animation: 'spin 1s linear infinite' }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

return (
    <div style={{ fontFamily: t.fontSans }}>
      {/* Tab navigation */}
      <div style={{ display: 'flex', borderBottom: `1px solid ${t.colors.border}`, marginBottom: '24px' }}>
        {[
          { id: 'store', label: 'Add-on Store' },
          { id: 'my-addons', label: `My Add-ons${myAddons.length > 0 ? ` (${myAddons.length})` : ''}` },
          {
            id: 'earn',
            label: `Earn Free Add-ons${
              reviewCredits.filter((c) => c.status === 'available').length > 0
                ? ` (${reviewCredits.filter((c) => c.status === 'available').length})`
                : ''
            }`,
          },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as any)}
            style={{
              padding: '10px 20px',
              fontSize: '0.875rem',
              fontWeight: 500,
              color: activeTab === tab.id ? t.colors.primary : t.colors.textTertiary,
              backgroundColor: 'transparent',
              border: 'none',
              borderBottom: activeTab === tab.id ? `2px solid ${t.colors.primary}` : '2px solid transparent',
              cursor: 'pointer',
              marginBottom: '-1px',
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* STORE TAB */}
      {activeTab === 'store' && (
        <div>
          {!hasPaymentMethod && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: '10px',
              padding: '12px 16px', marginBottom: '20px',
              backgroundColor: '#fef3c7', borderLeft: '4px solid #fcd34d',
              borderRadius: `0 ${t.radius.md} ${t.radius.md} 0`,
              fontSize: '0.875rem', color: '#92400e'
            }}>
              <AlertTriangle size={16} strokeWidth={1.5} color="#d97706" style={{ flexShrink: 0 }} />
              A payment method is required to purchase add-ons. Add one in the Billing Account tab.
            </div>
          )}

          {availableAddons.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '48px 24px', color: t.colors.textMuted, fontSize: '0.875rem' }}>
              No add-ons available yet.
            </div>
          ) : (
            <div style={{ display: 'grid', gap: '16px' }}>
              {availableAddons.map((addon) => {
                const qty = getQuantity(addon.id);
                const isPurchasing = purchasing === addon.id;
                const totalCost = parseFloat(String(addon.price_usd)) * qty;

                return (
                  <div
                    key={addon.id}
                    style={{
                      backgroundColor: t.colors.bgAlt,
                      border: `1px solid ${t.colors.border}`,
                      borderRadius: t.radius.md,
                      padding: '20px',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', flexWrap: 'wrap' }}>
                      <div style={{ flex: 1, minWidth: '200px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                          <Package size={18} strokeWidth={1.5} color={t.colors.primary} />
                          <h3 style={{ fontSize: '1rem', fontWeight: 600, color: t.colors.text, margin: 0 }}>
                            {addon.addon_name}
                          </h3>
                        </div>
                        {addon.description && (
                          <p style={{ fontSize: '0.8125rem', color: t.colors.textMuted, margin: '0 0 10px 0' }}>
                            {addon.description}
                          </p>
                        )}
                        <div style={{
                          display: 'inline-flex', alignItems: 'center', gap: '6px',
                          padding: '4px 10px',
                          backgroundColor: t.colors.primaryLight || '#ede9fe',
                          borderRadius: '9999px',
                          fontSize: '0.75rem', fontWeight: 600, color: t.colors.primary
                        }}>
                          {describeGrants(addon, qty)}
                        </div>
                        {addon.is_stackable && (
                          <p style={{ fontSize: '0.75rem', color: t.colors.textTertiary, margin: '8px 0 0 0' }}>
                            Stackable — purchase multiple for more
                          </p>
                        )}
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '12px' }}>
                        <div style={{ textAlign: 'right' }}>
                          <p style={{ fontSize: '1.5rem', fontWeight: 700, color: t.colors.text, margin: 0 }}>
                            ${totalCost.toFixed(2)}
                          </p>
                          <p style={{ fontSize: '0.75rem', color: t.colors.textTertiary, margin: '2px 0 0 0' }}>
                            one-time • lifetime
                          </p>
                        </div>

                        {addon.is_stackable && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ fontSize: '0.75rem', color: t.colors.textMuted }}>Qty:</span>
                            <div style={{ display: 'flex', alignItems: 'center', border: `1px solid ${t.colors.border}`, borderRadius: t.radius.sm, overflow: 'hidden' }}>
                              <button
                                onClick={() => setQuantities((prev) => ({ ...prev, [addon.id]: Math.max(1, (prev[addon.id] || 1) - 1) }))}
                                disabled={qty <= 1}
                                style={{ padding: '4px 10px', backgroundColor: t.colors.bg, border: 'none', cursor: qty <= 1 ? 'not-allowed' : 'pointer', color: t.colors.text, fontSize: '1rem', opacity: qty <= 1 ? 0.4 : 1 }}
                              >−</button>
                              <span style={{ padding: '4px 12px', fontSize: '0.875rem', fontWeight: 600, color: t.colors.text, borderLeft: `1px solid ${t.colors.border}`, borderRight: `1px solid ${t.colors.border}` }}>
                                {qty}
                              </span>
                              <button
                                onClick={() => setQuantities((prev) => ({ ...prev, [addon.id]: Math.min(50, (prev[addon.id] || 1) + 1) }))}
                                disabled={qty >= 50}
                                style={{ padding: '4px 10px', backgroundColor: t.colors.bg, border: 'none', cursor: qty >= 50 ? 'not-allowed' : 'pointer', color: t.colors.text, fontSize: '1rem', opacity: qty >= 50 ? 0.4 : 1 }}
                              >+</button>
                            </div>
                          </div>
                        )}

                        <button
onClick={() => { setPurchaseConfirm({ addon, quantity: qty }); setPurchaseConsent(false); }}
                          disabled={isPurchasing || !hasPaymentMethod}
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: '8px',
                            padding: '10px 20px',
                            backgroundColor: !hasPaymentMethod || isPurchasing ? t.colors.border : t.colors.primary,
                            color: !hasPaymentMethod || isPurchasing ? t.colors.textMuted : '#ffffff',
                            border: 'none', borderRadius: t.radius.md,
                            fontSize: '0.875rem', fontWeight: 600,
                            cursor: !hasPaymentMethod || isPurchasing ? 'not-allowed' : 'pointer',
                          }}
                        >
                          {isPurchasing ? (
                            <><Loader2 size={16} strokeWidth={2} style={{ animation: 'spin 1s linear infinite' }} /> Processing...</>
                          ) : (
                            <><ShoppingCart size={16} strokeWidth={1.5} /> Buy Now</>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Info note */}
          <div style={{
            display: 'flex', gap: '10px', alignItems: 'flex-start',
            marginTop: '24px', padding: '14px 16px',
            backgroundColor: t.colors.bgAlt, borderRadius: t.radius.md,
            border: `1px solid ${t.colors.border}`, fontSize: '0.8125rem', color: t.colors.textMuted
          }}>
            <Info size={16} strokeWidth={1.5} style={{ flexShrink: 0, marginTop: '1px' }} />
            <span>
              Add-ons are one-time lifetime purchases applied to this workspace. They do not unlock tier features or scheduled jobs — those require stacking main LTD codes. All purchases include a 30-day refund window.
            </span>
          </div>
        </div>
      )}

      {/* MY ADD-ONS TAB */}
      {activeTab === 'my-addons' && (
        <div>
          {myAddons.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '48px 24px' }}>
              <Package size={32} strokeWidth={1} color={t.colors.textMuted} style={{ marginBottom: '12px' }} />
              <p style={{ fontSize: '0.875rem', color: t.colors.textMuted, margin: 0 }}>
                No add-ons on this workspace yet.
              </p>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: '12px' }}>
              {myAddons.map((wla) => {
                const addon = wla.lifetimeAddon;
                const eligible = isRefundEligible(wla);
                const daysLeft = daysUntilRefundExpiry(wla);
                const isRefunding = refunding === wla.id;
                const sourceLabel = wla.source === 'purchase' ? 'Purchased' : wla.source === 'review_incentive' ? 'Review Bonus' : 'Admin Grant';

                return (
                  <div
                    key={wla.id}
                    style={{
                      backgroundColor: t.colors.bgAlt,
                      border: `1px solid ${t.colors.border}`,
                      borderRadius: t.radius.md,
                      padding: '16px 20px',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', flexWrap: 'wrap' }}>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
                          <CheckCircle size={16} strokeWidth={2} color={t.colors.success} />
                          <span style={{ fontSize: '0.9375rem', fontWeight: 600, color: t.colors.text }}>
                            {addon?.addon_name || 'Unknown add-on'}
                            {wla.quantity > 1 && (
                              <span style={{ fontWeight: 400, color: t.colors.textMuted }}> × {wla.quantity}</span>
                            )}
                          </span>
                        </div>
                        {addon && (
                          <p style={{ fontSize: '0.8125rem', color: t.colors.primary, margin: '0 0 8px 0' }}>
                            {describeGrants(addon, wla.quantity)}
                          </p>
                        )}
                        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                          <span style={{
                            fontSize: '0.6875rem', fontWeight: 600, padding: '2px 8px',
                            backgroundColor: t.colors.bgAlt, border: `1px solid ${t.colors.border}`,
                            borderRadius: '9999px', color: t.colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.04em'
                          }}>
                            {sourceLabel}
                          </span>
                          {wla.purchased_at && (
                            <span style={{ fontSize: '0.75rem', color: t.colors.textTertiary, display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <Clock size={12} strokeWidth={1.5} />
                              {new Date(wla.purchased_at).toLocaleDateString()}
                            </span>
                          )}
                          {eligible && daysLeft !== null && (
                            <span style={{ fontSize: '0.75rem', color: t.colors.success, fontWeight: 500 }}>
                              Refundable for {daysLeft} more day{daysLeft !== 1 ? 's' : ''}
                            </span>
                          )}
                        </div>
                      </div>

                      {eligible && wla.source === 'purchase' && (
                        <div>
                          {refundConfirm === wla.id ? (
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                              <span style={{ fontSize: '0.8125rem', color: t.colors.textMuted }}>Confirm refund?</span>
                              <button
                                onClick={() => handleRefund(wla)}
                                disabled={isRefunding}
                                style={{
                                  padding: '6px 14px', backgroundColor: t.colors.error, color: '#fff',
                                  border: 'none', borderRadius: t.radius.sm, fontSize: '0.8125rem',
                                  fontWeight: 600, cursor: isRefunding ? 'not-allowed' : 'pointer'
                                }}
                              >
                                {isRefunding ? <Loader2 size={14} strokeWidth={2} style={{ animation: 'spin 1s linear infinite' }} /> : 'Yes, refund'}
                              </button>
                              <button
                                onClick={() => setRefundConfirm(null)}
                                style={{ padding: '6px 10px', backgroundColor: 'transparent', border: `1px solid ${t.colors.border}`, borderRadius: t.radius.sm, cursor: 'pointer', color: t.colors.textMuted }}
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => setRefundConfirm(wla.id)}
                              style={{
                                padding: '6px 14px', backgroundColor: 'transparent',
                                color: t.colors.error, border: `1px solid ${t.colors.error}`,
                                borderRadius: t.radius.sm, fontSize: '0.8125rem',
                                fontWeight: 500, cursor: 'pointer'
                              }}
                            >
                              Refund
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* EARN FREE ADD-ONS TAB */}
      {activeTab === 'earn' && (
        <div>
          {/* Explanation banner */}
          <div style={{
            padding: '16px 20px', marginBottom: '20px',
            backgroundColor: t.colors.bgAlt,
            border: `1px solid ${t.colors.border}`,
            borderLeft: `4px solid ${t.colors.primary}`,
            borderRadius: `0 ${t.radius.md} ${t.radius.md} 0`,
            fontSize: '0.875rem', color: t.colors.text, lineHeight: 1.6,
          }}>
            <p style={{ margin: '0 0 8px 0', fontWeight: 600, fontSize: '0.9375rem' }}>
              Earn free lifetime add-ons by reviewing DashboardFox.
            </p>
            <ul style={{ margin: '0 0 10px 0', paddingLeft: '20px', color: t.colors.textMuted }}>
              <li>1 approved review: +1 Custom Domain, +1 Branding Policy</li>
              <li>2 approved reviews: adds +5 Public View Licenses</li>
              <li>All 3 platforms (sweep bonus): adds +2 Named Users and +5 more Public View Licenses</li>
            </ul>
            <p style={{ margin: 0, fontSize: '0.8125rem', color: t.colors.textMuted }}>
              Approvals within 2 business days.{' '}
<a              
                href="https://dashboardfox.com/legal/review-program"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: t.colors.primary, textDecoration: 'none', fontWeight: 500 }}
              >
                Read the rules →
              </a>
            </p>
          </div>

          {/* 14-day gate warning — shown only when within gate */}
          {!canSubmitReview.canSubmit && canSubmitReview.reason === 'within_14_day_gate' && canSubmitReview.unlocksAt && (
            <div style={{
              display: 'flex', alignItems: 'flex-start', gap: '10px',
              padding: '12px 16px', marginBottom: '20px',
              backgroundColor: '#fef3c7', borderLeft: '4px solid #fcd34d',
              borderRadius: `0 ${t.radius.md} ${t.radius.md} 0`,
              fontSize: '0.875rem', color: '#92400e',
            }}>
              <Clock size={16} strokeWidth={1.5} color="#d97706" style={{ flexShrink: 0, marginTop: '1px' }} />
              <span>
                Review submissions unlock on{' '}
                <strong>{new Date(canSubmitReview.unlocksAt).toLocaleDateString()}</strong>.
                This helps ensure reviews reflect real usage.
              </span>
            </div>
          )}

          {/* Platform cards — G2, Capterra, SourceForge */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: '12px',
            marginBottom: '24px',
          }}>
            {([
              { id: 'g2', name: 'G2', url: 'https://www.g2.com/products/dashboardfox/' },
              { id: 'capterra', name: 'Capterra', url: 'https://www.capterra.com/p/202379/DashboardFox/' },
              { id: 'sourceforge', name: 'SourceForge', url: 'https://sourceforge.net/software/product/DashboardFox/' },
            ] as const).map((platform) => {
              const subs = reviewSubmissions.filter((s) => s.platform === platform.id);
              const active = subs.find((s) => s.status === 'pending' || s.status === 'approved');
              const banned = subs.find((s) => s.rejection_type === 'platform_banned');
              const platformRejectedCount = subs.filter((s) => s.rejection_type === 'platform_rejected').length;

              let statusLabel = 'Not yet started';
              let statusColor = t.colors.textMuted;
              let canSubmitThisPlatform = true;
              let disabledReason: string | null = null;

              if (banned) {
                statusLabel = 'Platform closed for your account';
                statusColor = t.colors.error;
                canSubmitThisPlatform = false;
                disabledReason = 'This platform is permanently closed for your account.';
              } else if (platformRejectedCount >= 2) {
                statusLabel = 'Retry limit reached';
                statusColor = t.colors.error;
                canSubmitThisPlatform = false;
                disabledReason = 'Maximum retries reached for this platform.';
              } else if (active?.status === 'pending') {
                statusLabel = 'Pending review';
                statusColor = t.colors.warning;
                canSubmitThisPlatform = false;
                disabledReason = 'Your submission is awaiting admin review.';
              } else if (active?.status === 'approved') {
                statusLabel = 'Approved ✓';
                statusColor = t.colors.success;
                canSubmitThisPlatform = false;
                disabledReason = 'Your review has been approved.';
              } else if (subs.length > 0) {
                statusLabel = 'Resubmit available';
                statusColor = t.colors.textMuted;
              }

              const globalGate = !canSubmitReview.canSubmit;
              const effectivelyCanSubmit = canSubmitThisPlatform && !globalGate;

              return (
                <div key={platform.id} style={{
                  backgroundColor: t.colors.bgAlt,
                  border: `1px solid ${t.colors.border}`,
                  borderRadius: t.radius.md,
                  padding: '16px',
                  display: 'flex', flexDirection: 'column', gap: '10px',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Package size={16} strokeWidth={1.5} color={t.colors.primary} />
                    <h4 style={{ fontSize: '0.9375rem', fontWeight: 600, color: t.colors.text, margin: 0 }}>
                      {platform.name}
                    </h4>
                  </div>

                  <div style={{ fontSize: '0.8125rem', color: statusColor, fontWeight: 500 }}>
                    {statusLabel}
                  </div>

                  <a href={platform.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.8125rem', color: t.colors.primary, textDecoration: 'none' }}>
                    Go to {platform.name} →
                  </a>

                  {mode === 'widget' ? (
                    <div style={{ fontSize: '0.75rem', color: t.colors.textTertiary, fontStyle: 'italic', marginTop: '4px' }}>
                      Submit in management dashboard →
                    </div>
                  ) : (
                    <>
                      <button
                        onClick={() => setSubmitModal(platform.id)}
                        disabled={!effectivelyCanSubmit}
                        style={{
                          marginTop: '4px',
                          padding: '8px 12px',
                          backgroundColor: effectivelyCanSubmit ? t.colors.primary : t.colors.border,
                          color: effectivelyCanSubmit ? '#ffffff' : t.colors.textMuted,
                          border: 'none',
                          borderRadius: t.radius.sm,
                          fontSize: '0.8125rem',
                          fontWeight: 600,
                          cursor: effectivelyCanSubmit ? 'pointer' : 'not-allowed',
                        }}
                      >
                        Submit URL
                      </button>
                      {disabledReason && (
                        <div style={{ fontSize: '0.75rem', color: t.colors.textTertiary, marginTop: '-2px' }}>
                          {disabledReason}
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>

          {/* Credits list — hidden when user has no credits yet */}
          {reviewCredits.length > 0 && (
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '0.9375rem', fontWeight: 600, color: t.colors.text, margin: '0 0 12px 0' }}>
                My Credits
              </h3>

              {mode === 'widget' && (
                <div style={{ padding: '12px 16px', marginBottom: '12px', backgroundColor: t.colors.bgAlt, border: `1px solid ${t.colors.border}`, borderRadius: t.radius.md, fontSize: '0.8125rem', color: t.colors.textMuted }}>
                  Credits can be redeemed from the management dashboard.{' '}
                  <a href="/dashboard/subscription" target="_blank" rel="noopener noreferrer" style={{ color: t.colors.primary, textDecoration: 'none', fontWeight: 500 }}>Open dashboard →</a>
                </div>
              )}

              <div style={{ display: 'grid', gap: '10px' }}>
                {reviewCredits.map((credit) => {
                  const isAvailable = credit.status === 'available';
                  const isRedeemed = credit.status === 'redeemed';
                  const isVoided = credit.status === 'voided';
                  const addon = credit.lifetimeAddon;
                  const platform = credit.sourceSubmission?.platform;
                  const platformLabel = platform === 'g2' ? 'G2' : platform === 'capterra' ? 'Capterra' : platform === 'sourceforge' ? 'SourceForge' : 'review';
                  const eligibleWorkspaces = (myWorkspaces || []).filter((w) => w.hasActiveLtdCode);
                  const canRedeem = mode !== 'widget' && isAvailable && eligibleWorkspaces.length > 0;
                  const selectedWsId = selectedWorkspace[credit.id];
                  const isRedeemingThis = redeeming === credit.id;
                  const thisError = redeemError[credit.id];

                  return (
                    <div key={credit.id} style={{
                      backgroundColor: t.colors.bgAlt,
                      border: `1px solid ${t.colors.border}`,
                      borderRadius: t.radius.md,
                      padding: '14px 16px',
                      opacity: (isRedeemed || isVoided) ? 0.7 : 1,
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap' }}>
                        <div style={{ flex: 1, minWidth: '200px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                            <CheckCircle size={14} strokeWidth={2} color={isAvailable ? t.colors.success : t.colors.textMuted} />
                            <span style={{ fontSize: '0.875rem', fontWeight: 600, color: t.colors.text }}>
                              {addon?.addon_name || 'Review bonus'}
                            </span>
                          </div>
                          {addon && (
                            <p style={{ fontSize: '0.8125rem', color: t.colors.primary, margin: '0 0 4px 0' }}>
                              {describeGrants(addon as any, 1)}
                            </p>
                          )}
                          <p style={{ fontSize: '0.75rem', color: t.colors.textTertiary, margin: 0 }}>
                            From: {platformLabel} review
                            {isRedeemed && credit.redeemed_at && (
                              <span> • Redeemed on {new Date(credit.redeemed_at).toLocaleDateString()}</span>
                            )}
                            {isVoided && (
                              <span> • Voided</span>
                            )}
                          </p>
                        </div>

                        {isAvailable && mode !== 'widget' && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'flex-end' }}>
                            {eligibleWorkspaces.length === 0 ? (
                              <p style={{ fontSize: '0.75rem', color: t.colors.textTertiary, fontStyle: 'italic', margin: 0, maxWidth: '260px', textAlign: 'right' }}>
                                You need at least one workspace with an active lifetime code to redeem credits.
                              </p>
                            ) : (
                              <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                                <select
                                  value={selectedWsId || ''}
                                  onChange={(e) => setSelectedWorkspace((prev) => ({ ...prev, [credit.id]: parseInt(e.target.value) }))}
                                  disabled={isRedeemingThis}
                                  style={{ padding: '6px 10px', fontSize: '0.8125rem', border: `1px solid ${t.colors.border}`, borderRadius: t.radius.sm, backgroundColor: t.colors.bg, color: t.colors.text, cursor: isRedeemingThis ? 'not-allowed' : 'pointer' }}
                                >
                                  <option value="">Choose workspace...</option>
                                  {eligibleWorkspaces.map((w) => (
                                    <option key={w.id} value={w.id}>{w.name}</option>
                                  ))}
                                </select>
                                <button
                                  onClick={() => handleRedeemCredit(credit.id)}
                                  disabled={!canRedeem || !selectedWsId || isRedeemingThis}
                                  style={{
                                    padding: '6px 14px',
                                    backgroundColor: (!canRedeem || !selectedWsId || isRedeemingThis) ? t.colors.border : t.colors.primary,
                                    color: (!canRedeem || !selectedWsId || isRedeemingThis) ? t.colors.textMuted : '#ffffff',
                                    border: 'none', borderRadius: t.radius.sm,
                                    fontSize: '0.8125rem', fontWeight: 600,
                                    cursor: (!canRedeem || !selectedWsId || isRedeemingThis) ? 'not-allowed' : 'pointer',
                                  }}
                                >
                                  {isRedeemingThis ? 'Redeeming...' : 'Redeem'}
                                </button>
                              </div>
                            )}
                            {thisError && (
                              <p style={{ fontSize: '0.75rem', color: t.colors.error, margin: 0, maxWidth: '260px', textAlign: 'right' }}>
                                {thisError}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Submission history — hidden when user has no submissions yet */}
          {reviewSubmissions.length > 0 && (
            <div>
              <h3 style={{ fontSize: '0.9375rem', fontWeight: 600, color: t.colors.text, margin: '0 0 12px 0' }}>
                Submission History
              </h3>
              <div style={{ display: 'grid', gap: '8px' }}>
                {[...reviewSubmissions]
                  .sort((a, b) => new Date(b.submitted_at).getTime() - new Date(a.submitted_at).getTime())
                  .map((sub) => {
                    const platformLabel = sub.platform === 'g2' ? 'G2' : sub.platform === 'capterra' ? 'Capterra' : 'SourceForge';

                    // Status label + color
                    let statusLabel = '';
                    let statusColor = t.colors.textMuted;
                    if (sub.status === 'approved') {
                      statusLabel = 'Approved';
                      statusColor = t.colors.success;
                    } else if (sub.status === 'pending') {
                      statusLabel = 'Pending';
                      statusColor = t.colors.warning;
                    } else if (sub.status === 'rejected') {
                      statusLabel = 'Rejected';
                      statusColor = t.colors.error;
                    }

                    // Rejection-type friendly label
                    const rejectionLabel =
                      sub.rejection_type === 'admin_qa_failed' ? 'Admin QA failed' :
                      sub.rejection_type === 'platform_rejected' ? 'Platform rejected' :
                      sub.rejection_type === 'not_yet_published' ? 'Not yet published' :
                      sub.rejection_type === 'platform_banned' ? 'Platform banned' : null;

                    // Is resubmit eligible for this platform? Recomputed inline (same logic as platform cards)
                    const platformSubs = reviewSubmissions.filter((s) => s.platform === sub.platform);
                    const active = platformSubs.find((s) => s.status === 'pending' || s.status === 'approved');
                    const banned = platformSubs.find((s) => s.rejection_type === 'platform_banned');
                    const platformRejectedCount = platformSubs.filter((s) => s.rejection_type === 'platform_rejected').length;
                    const canResubmit =
                      sub.status === 'rejected' &&
                      mode !== 'widget' &&
                      !banned &&
                      !active &&
                      platformRejectedCount < 2 &&
                      canSubmitReview.canSubmit;

                    return (
                      <div key={sub.id} style={{
                        backgroundColor: t.colors.bgAlt,
                        border: `1px solid ${t.colors.border}`,
                        borderRadius: t.radius.sm,
                        padding: '10px 14px',
                        fontSize: '0.8125rem',
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px', flexWrap: 'wrap' }}>
                          <div style={{ flex: 1, minWidth: '200px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px' }}>
                              <span style={{ fontWeight: 600, color: t.colors.text }}>{platformLabel}</span>
                              <span style={{ color: t.colors.textTertiary }}>•</span>
                              <span style={{ fontWeight: 500, color: statusColor }}>{statusLabel}</span>
                              <span style={{ color: t.colors.textTertiary, fontSize: '0.75rem' }}>
                                ({new Date(sub.submitted_at).toLocaleDateString()})
                              </span>
                            </div>
                            {sub.status === 'rejected' && rejectionLabel && (
                              <p style={{ fontSize: '0.75rem', color: t.colors.textMuted, margin: '2px 0 0 0' }}>
                                {rejectionLabel}
                                {sub.rejection_reason ? `: ${sub.rejection_reason}` : ''}
                              </p>
                            )}
                            <a href={sub.review_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.75rem', color: t.colors.primary, textDecoration: 'none', display: 'inline-block', marginTop: '2px' }}>
                              View review →
                            </a>
                          </div>

                          {canResubmit && (
                            <button
                              onClick={() => setSubmitModal(sub.platform)}
                              style={{
                                padding: '5px 12px',
                                backgroundColor: 'transparent',
                                color: t.colors.primary,
                                border: `1px solid ${t.colors.primary}`,
                                borderRadius: t.radius.sm,
                                fontSize: '0.75rem', fontWeight: 500,
                                cursor: 'pointer',
                              }}
                            >
                              Resubmit
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Submit Review Modal */}
      {submitModal && (
        <div style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1000, padding: '24px',
        }}>
          <div style={{
            backgroundColor: t.colors.bg, borderRadius: t.radius.lg,
            border: `1px solid ${t.colors.border}`, padding: '28px',
            maxWidth: '500px', width: '100%', fontFamily: t.fontSans,
          }}>
            <h2 style={{ fontSize: '1.125rem', fontWeight: 600, color: t.colors.text, margin: '0 0 4px 0' }}>
              Submit {submitModal === 'g2' ? 'G2' : submitModal === 'capterra' ? 'Capterra' : 'SourceForge'} Review
            </h2>
            <p style={{ fontSize: '0.8125rem', color: t.colors.textMuted, margin: '0 0 20px 0' }}>
              Paste the URL of your published review. Admin approval typically takes up to 2 business days.
            </p>

            <div style={{ marginBottom: '14px' }}>
              <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: 500, color: t.colors.text, marginBottom: '6px' }}>
                Review URL <span style={{ color: t.colors.error }}>*</span>
              </label>
              <input type="url" value={reviewUrl} onChange={(e) => setReviewUrl(e.target.value)} placeholder="https://..." disabled={submitting} style={{ width: '100%', padding: '9px 12px', fontSize: '0.875rem', border: `1px solid ${t.colors.border}`, borderRadius: t.radius.sm, backgroundColor: t.colors.bg, color: t.colors.text, boxSizing: 'border-box' }} />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: 500, color: t.colors.text, marginBottom: '6px' }}>
                Screenshot URL <span style={{ color: t.colors.textTertiary, fontWeight: 400 }}>(optional)</span>
              </label>
              <input type="url" value={screenshotUrl} onChange={(e) => setScreenshotUrl(e.target.value)} placeholder="https://..." disabled={submitting} style={{ width: '100%', padding: '9px 12px', fontSize: '0.875rem', border: `1px solid ${t.colors.border}`, borderRadius: t.radius.sm, backgroundColor: t.colors.bg, color: t.colors.text, boxSizing: 'border-box' }} />
              <p style={{ fontSize: '0.75rem', color: t.colors.textTertiary, margin: '4px 0 0 0' }}>
                Helps admin verify if the platform review is hard to find later.
              </p>
            </div>

            <div
              style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '16px', cursor: submitting ? 'not-allowed' : 'pointer' }}
              onClick={() => !submitting && setSubmitConsent(!submitConsent)}
            >
              <div style={{
                width: '18px', height: '18px', flexShrink: 0, marginTop: '2px',
                border: `2px solid ${submitConsent ? t.colors.primary : t.colors.border}`,
                borderRadius: '4px',
                backgroundColor: submitConsent ? t.colors.primary : 'transparent',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {submitConsent && <span style={{ color: '#fff', fontSize: '11px', fontWeight: 700, lineHeight: 1 }}>✓</span>}
              </div>
              <p style={{ fontSize: '0.8125rem', color: t.colors.textMuted, margin: 0, lineHeight: 1.5 }}>
                I confirm this review is published, written by me, and reflects my honest experience. I understand incentives will be disclosed per the platform's policy.
              </p>
            </div>

            {submitError && (
              <div style={{ padding: '10px 12px', marginBottom: '14px', backgroundColor: t.colors.errorBg, border: `1px solid ${t.colors.error}`, borderRadius: t.radius.sm, fontSize: '0.8125rem', color: t.colors.error }}>
                {submitError}
              </div>
            )}

            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={closeSubmitModal}
                disabled={submitting}
                style={{
                  flex: 1, padding: '10px 16px',
                  backgroundColor: t.colors.bg, color: t.colors.text,
                  border: `1px solid ${t.colors.border}`, borderRadius: t.radius.md,
                  fontSize: '0.875rem', fontWeight: 500,
                  cursor: submitting ? 'not-allowed' : 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitReview}
                disabled={submitting || !isValidUrl(reviewUrl) || !submitConsent || (!!screenshotUrl && !isValidUrl(screenshotUrl))}
                style={{
                  flex: 1, padding: '10px 16px',
                  backgroundColor: (submitting || !isValidUrl(reviewUrl) || !submitConsent || (!!screenshotUrl && !isValidUrl(screenshotUrl))) ? t.colors.border : t.colors.primary,
                  color: (submitting || !isValidUrl(reviewUrl) || !submitConsent || (!!screenshotUrl && !isValidUrl(screenshotUrl))) ? t.colors.textMuted : '#ffffff',
                  border: 'none', borderRadius: t.radius.md,
                  fontSize: '0.875rem', fontWeight: 600,
                  cursor: (submitting || !isValidUrl(reviewUrl) || !submitConsent || (!!screenshotUrl && !isValidUrl(screenshotUrl))) ? 'not-allowed' : 'pointer',
                }}
              >
                {submitting ? 'Submitting...' : 'Submit for Review'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Purchase Confirmation Modal */}
      {purchaseConfirm && (
        <div style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1000, padding: '24px'
        }}>
          <div style={{
            backgroundColor: t.colors.bg, borderRadius: t.radius.lg,
            border: `1px solid ${t.colors.border}`, padding: '32px',
            maxWidth: '440px', width: '100%', fontFamily: t.fontSans
          }}>
            <h2 style={{ fontSize: '1.125rem', fontWeight: 600, color: t.colors.text, margin: '0 0 8px 0' }}>
              Confirm Purchase
            </h2>
            <p style={{ fontSize: '0.875rem', color: t.colors.textMuted, margin: '0 0 20px 0' }}>
              You are about to make a one-time lifetime purchase.
            </p>
            <div style={{
              backgroundColor: t.colors.bgAlt, borderRadius: t.radius.md,
              border: `1px solid ${t.colors.border}`, padding: '16px', marginBottom: '20px'
            }}>
              <p style={{ fontSize: '0.9375rem', fontWeight: 600, color: t.colors.text, margin: '0 0 4px 0' }}>
                {purchaseConfirm.addon.addon_name}
                {purchaseConfirm.quantity > 1 && ` × ${purchaseConfirm.quantity}`}
              </p>
              <p style={{ fontSize: '0.8125rem', color: t.colors.primary, margin: '0 0 12px 0' }}>
                {describeGrants(purchaseConfirm.addon, purchaseConfirm.quantity)}
              </p>
<p style={{ fontSize: '1.25rem', fontWeight: 700, color: t.colors.text, margin: 0 }}>
                ${cfTotal.toFixed(2)}
                <span style={{ fontSize: '0.8125rem', fontWeight: 400, color: t.colors.textMuted, marginLeft: '4px' }}>
                  one-time • never expires
                </span>
              </p>
              {estimateLoading && (
                <p style={{ fontSize: '0.75rem', color: t.colors.textMuted, margin: '6px 0 0 0' }}>Calculating tax…</p>
              )}
              {!estimateLoading && cfTax > 0 && (
                <p style={{ fontSize: '0.75rem', color: t.colors.textMuted, margin: '6px 0 0 0' }}>
                  Subtotal ${cfSub.toFixed(2)} + VAT ${cfTax.toFixed(2)}
                </p>
              )}
            </div>
            <div style={{
              padding: '14px 16px', marginBottom: '20px',
              backgroundColor: '#fef3c7', borderLeft: '4px solid #fcd34d',
              borderRadius: `0 ${t.radius.sm} ${t.radius.sm} 0`,
              fontSize: '0.8125rem', color: '#92400e'
            }}>
By confirming, your payment method on file will be charged ${cfTotal.toFixed(2)}{cfTax > 0 ? ' (incl. VAT)' : ''} as a one-time charge. This purchase is non-recurring and will be applied permanently to your workspace.
            </div>
{/* Consent checkbox */}
            <div
              style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '20px', cursor: 'pointer' }}
              onClick={() => setPurchaseConsent(!purchaseConsent)}
            >
              <div style={{
                width: '20px', height: '20px', flexShrink: 0, marginTop: '2px',
                border: `2px solid ${purchaseConsent ? t.colors.primary : t.colors.border}`,
                borderRadius: '4px',
                backgroundColor: purchaseConsent ? t.colors.primary : 'transparent',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.15s ease'
              }}>
                {purchaseConsent && <span style={{ color: '#fff', fontSize: '12px', fontWeight: 700, lineHeight: 1 }}>✓</span>}
              </div>
              <p style={{ fontSize: '0.8125rem', color: t.colors.textMuted, margin: 0, lineHeight: 1.6 }}>
I agree to this one-time charge of ${cfTotal.toFixed(2)}{cfTax > 0 ? ' (incl. VAT)' : ''}. I understand this purchase is non-recurring, permanently applied to my workspace, and subject to the <a href="https://dashboardfox.com/legal/terms/#refunds" target="_blank" style={{ color: t.colors.primary }}>refund policy</a>.
              </p>
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => setPurchaseConfirm(null)}
                disabled={purchasing === purchaseConfirm.addon.id}
                style={{
                  flex: 1, padding: '10px 16px', backgroundColor: t.colors.bg,
                  color: t.colors.text, border: `1px solid ${t.colors.border}`,
                  borderRadius: t.radius.md, fontSize: '0.875rem', fontWeight: 500,
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  const { addon, quantity } = purchaseConfirm;
                  setPurchaseConfirm(null);
                  await handlePurchase({ ...addon, _confirmedQty: quantity } as any);
                }}
disabled={purchasing === purchaseConfirm.addon.id || !purchaseConsent}
                style={{
                  flex: 1, padding: '10px 16px', backgroundColor: !purchaseConsent || purchasing === purchaseConfirm.addon.id ? t.colors.border : t.colors.primary,
color: !purchaseConsent || purchasing === purchaseConfirm.addon.id ? t.colors.textMuted : '#ffffff',
                  border: 'none', borderRadius: t.radius.md,
                  fontSize: '0.875rem', fontWeight: 600,
                  cursor: !purchaseConsent || purchasing === purchaseConfirm.addon.id ? 'not-allowed' : 'pointer'
                }}
              >
                {purchasing === purchaseConfirm.addon.id ? 'Processing...' : 'Confirm Purchase'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default LifetimeAddonStoreCore;
